import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import {
  GeodataMarker,
  GeodataMarkerEstimate,
} from '../../../../../../typings/api/skymap/rest/v0/geodata';
import { GcpModel } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { useElementOverflow } from '../../../hooks/use-element-overflow';
import { Icon } from '../../icon/icon';
import { GeodataImageItem } from '../geodata-images-state';
import { MarkerEstimateThumbnail } from './marker-estimate-thumbnail';

interface MarkerEstimateCardProps {
  markerId: string;
  gcp: GcpModel;
  image: GeodataImageItem;
  estimate?: Required<GeodataMarkerEstimate>;
  marker: GeodataMarker;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'PINNED' | 'UNPINNED';
  geodataId: string;
  zoomLevel: number;
  disabled: boolean;
  onAccept: (estimateId: string) => void | Promise<void>;
  onReject: (estimateId: string) => void | Promise<void>;
  onPin: (
    markerId: string,
    gcpId: string,
    imageId: string,
    position: [number, number],
  ) => void | Promise<void>;
  onUnpin: (
    markerId: string,
    gcpId: string,
    imageId: string,
    position: [number, number],
  ) => void | Promise<void>;
  onImageClick: () => void;
}

export const MarkerEstimateCard = (props: MarkerEstimateCardProps) => {
  const gcpNameRef = useElementOverflow<HTMLDivElement>();
  const imageNameRef = useElementOverflow<HTMLDivElement>();

  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  const isPinned = props.status === 'PINNED';
  const isUnpinned = props.status === 'UNPINNED';
  const isPinnedOrUnpinned = isPinned || isUnpinned;
  const isAccepted = props.status === 'ACCEPTED';
  const isRejected = props.status === 'REJECTED';

  // For PINNED/UNPINNED, use marker position; otherwise use estimate position
  const position = isPinnedOrUnpinned ? props.marker.position : props.estimate?.position;

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPinnedOrUnpinned) {
      if (isUnpinned && position && props.gcp.id && props.image.imageId) {
        setAcceptLoading(true);
        try {
          await Promise.resolve(
            props.onPin(props.marker.id, props.gcp.id, props.image.imageId, position),
          );
        } finally {
          setAcceptLoading(false);
        }
      }
    } else if (props.estimate?.id && !isAccepted) {
      setAcceptLoading(true);
      try {
        await Promise.resolve(props.onAccept(props.estimate.id));
      } finally {
        setAcceptLoading(false);
      }
    }
  };

  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPinnedOrUnpinned) {
      if (isPinned && position && props.gcp.id && props.image.imageId) {
        setRejectLoading(true);
        try {
          await Promise.resolve(
            props.onUnpin(props.marker.id, props.gcp.id, props.image.imageId, position),
          );
        } finally {
          setRejectLoading(false);
        }
      }
    } else if (props.estimate?.id && !isRejected) {
      setRejectLoading(true);
      try {
        await Promise.resolve(props.onReject(props.estimate.id));
      } finally {
        setRejectLoading(false);
      }
    }
  };

  // Button disabled states (including while loading)
  const acceptDisabled =
    props.disabled || acceptLoading || (isPinnedOrUnpinned ? isPinned : isAccepted);
  const rejectDisabled =
    props.disabled || rejectLoading || (isPinnedOrUnpinned ? isUnpinned : isRejected);

  return (
    <CardContainer>
      <GcpName ref={gcpNameRef.ref} title={gcpNameRef.isOverflowed ? props.gcp.name : ''}>
        {props.gcp.name}
      </GcpName>
      <ThumbnailContainer>
        {position && (
          <MarkerEstimateThumbnail
            estimate={props.estimate}
            height={120}
            image={props.image}
            position={position}
            status={props.status}
            width={120}
            zoomLevel={props.zoomLevel}
            onClick={props.onImageClick}
          />
        )}
        {props.estimate != null && (
          <ConfidenceBadge>{(props.estimate.confidence * 100).toFixed(2)}%</ConfidenceBadge>
        )}
        <AcceptIconButton
          aria-disabled={acceptDisabled}
          type="button"
          onClick={acceptDisabled ? undefined : handleAccept}
        >
          <Icon
            fixedWidth={true}
            icon={acceptLoading ? ['fas', 'spinner'] : ['fas', 'check']}
            spin={acceptLoading}
          />
        </AcceptIconButton>
        <RejectIconButton
          aria-disabled={rejectDisabled}
          type="button"
          onClick={rejectDisabled ? undefined : handleReject}
        >
          <Icon
            fixedWidth={true}
            icon={rejectLoading ? ['fas', 'spinner'] : ['fas', 'times']}
            spin={rejectLoading}
          />
        </RejectIconButton>
      </ThumbnailContainer>
      <ImageName
        ref={imageNameRef.ref}
        title={imageNameRef.isOverflowed ? props.image.displayName : ''}
        onClick={props.onImageClick}
      >
        {props.image.displayName}
      </ImageName>
    </CardContainer>
  );
};

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25em;
  flex-shrink: 0;
  background-color: #fafafa;
  border-radius: 5px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 1px 2px rgba(0, 0, 0, 0.24);
  padding: 0.5em;
`;

const ThumbnailContainer = styled.div`
  width: 120px;
  height: 120px;
  background-color: #424242;
  border-radius: 3px;
  position: relative;
  overflow: hidden;
`;

const ConfidenceBadge = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  padding: 2px 6px;
  font-size: 11px;
  color: #ffffff;
  background-color: ${({ theme }) => theme.color.gray.dark};
  border-radius: 0 0 4px 0;
  z-index: 1;
`;

const GcpName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875em;
  font-weight: 500;
  width: 120px;
  text-align: center;
`;

const ImageName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75em;
  color: #666;
  width: 120px;
  text-align: center;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #333;
    text-decoration: underline;
  }
`;

const iconButtonBaseStyles = css`
  position: absolute;
  cursor: pointer;
  padding: 4px;
  border-radius: 3px;
  transition: opacity 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not([aria-disabled='true']) {
    opacity: 0.8;
  }

  &[aria-disabled='true'] {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const AcceptIconButton = styled.button`
  ${iconButtonBaseStyles}
  bottom: 4px;
  left: 4px;
  color: #1b5e20;
  background-color: ${({ theme }) => theme.color.green};
  font-size: 0.75em;
  border: none;
`;

const RejectIconButton = styled.button`
  ${iconButtonBaseStyles}
  bottom: 4px;
  right: 4px;
  color: #8b0000;
  background-color: ${({ theme }) => theme.color.red};
  font-size: 0.75em;
  border: none;
`;
